import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { TownChips } from "@/components/calc/TownChips";

export default function ManageAlertsPage() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    document.title = "Manage my towns · The Expat Studio";
    if (!token) { setErr("This link is missing its code. Please use the 'Manage my towns' link from one of our emails."); return; }
    api.get(`/public/alerts/${token}`).then((r) => { setD(r.data); setPicked(r.data.watching); }).catch((e) => setErr(errMsg(e)));
  }, [token]);
  const dirty = useMemo(() => d && [...picked].sort().join() !== [...d.watching].sort().join(), [d, picked]);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put(`/public/alerts/${token}`, { slugs: picked }); setD({ ...d, watching: r.data.watching, unsubscribed: false }); toast.success("Saved your towns"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen paper-grain" data-testid="manage-alerts-page">
      <header className="border-b border-[#E2D9CC]"><div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
        <a href="/calculators" className="font-display text-xl text-[#3B1B48]" data-testid="alerts-brand-link">The Expat Studio</a>
        <a href="/calculators" className="text-sm text-[#8C827A] hover:text-[#3B1B48]">Free calculators</a>
      </div></header>
      <main className="max-w-4xl mx-auto px-6 py-14 space-y-8">
        <div className="rise">
          <p className="eyebrow mb-3 flex items-center gap-2"><Bell className="w-4 h-4" />Price alerts</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#3B1B48]">Manage my towns</h1>
          {d && <p className="text-[#6B5B4E] mt-4" data-testid="alerts-intro">We'll email <strong>{d.email}</strong> when asking prices in a town you pick move {d.threshold}% or more since your last alert.</p>}
          {d?.unsubscribed && <p className="text-sm text-[#A4462A] mt-2" data-testid="alerts-unsubscribed">You're currently unsubscribed. Saving your towns turns emails back on.</p>}
        </div>
        {err && <p data-testid="alerts-error" className="text-red-700">{err}</p>}
        {!d && !err && <p className="eyebrow" data-testid="alerts-loading">Loading…</p>}
        {d && (<>
          <TownChips towns={d.towns} picked={picked} onChange={setPicked} />
          <div className="flex items-center gap-4">
            <button onClick={save} disabled={busy || !dirty} data-testid="alerts-save" className="h-12 px-6 bg-[#3B1B48] text-[#FAF7F2] text-sm hover:bg-[#2A1334] transition-colors disabled:opacity-50">{busy ? "Saving…" : "Save my towns"}</button>
            {!dirty && <span className="text-sm text-[#2D6A4F] flex items-center gap-1" data-testid="alerts-saved"><Check className="w-4 h-4" />Watching {d.watching.length} town{d.watching.length === 1 ? "" : "s"}</span>}
          </div>
        </>)}
      </main>
    </div>
  );
}
